import { useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { PreJobCheckListV2FormValues } from "./PreJobCheckListV2Dialog";
import { writeDraft } from "../../lib/inspectionDraftStore";
import { countFilledFields, inspectionClosedUnexpectedly, inspectionDraftSaved } from "../../lib/inspectionTelemetry";

/** How long the tech can pause before we commit what they've typed. */
const DEBOUNCE_MS = 800;

/**
 * Sentry keeps a bounded breadcrumb buffer, and a 15-minute inspection would
 * otherwise emit hundreds of save crumbs and push out everything useful that
 * happened before them. One per 30s is enough to see that autosave was alive.
 */
const BREADCRUMB_THROTTLE_MS = 30000;

/**
 * Renderless. Persists the inspection form as the tech fills it in.
 *
 * Mounted inside the Formik tree so it can watch `values` directly. Writes are
 * debounced while the tech types and committed unconditionally when the form
 * goes away — an unmount is precisely the case this exists to survive, so the
 * save on teardown matters more than any of the debounced writes before it.
 *
 * Nothing here ever deletes a draft. Drafts outlive an unmount, a closed dialog,
 * a backgrounded app and a browser restart on purpose; only a successful submit
 * clears one.
 */
export const InspectionDraftAutosave = ({ jobId, enabled }: { jobId: string; enabled: boolean }) => {
  const { values } = useFormikContext<PreJobCheckListV2FormValues>();

  // Refs so the save-on-unmount effect can read the latest state without
  // re-subscribing (and therefore tearing down) on every keystroke.
  const valuesRef = useRef(values);
  const enabledRef = useRef(enabled);
  const openedAtRef = useRef<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastBreadcrumbAtRef = useRef(0);

  valuesRef.current = values;
  enabledRef.current = enabled;

  if (enabled && openedAtRef.current === null) openedAtRef.current = Date.now();
  if (!enabled) openedAtRef.current = null;

  const saveNow = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (!enabledRef.current || !jobId) return;

    writeDraft(jobId, valuesRef.current);

    const now = Date.now();
    if (now - lastBreadcrumbAtRef.current > BREADCRUMB_THROTTLE_MS) {
      lastBreadcrumbAtRef.current = now;
      inspectionDraftSaved(jobId, countFilledFields(valuesRef.current));
    }
  };

  // Debounced write on every change to the form.
  useEffect(() => {
    if (!enabled || !jobId) return undefined;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(saveNow, DEBOUNCE_MS);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, enabled, jobId]);

  // Backgrounding the app or reloading the page kills pending debounced writes,
  // so WRITE synchronously on the way out — this saves the draft, it never
  // discards it. Closing and reopening the app is exactly the case that has to
  // come back with the technician's work intact. `visibilitychange` is the only
  // one of these that fires reliably on mobile Safari.
  useEffect(() => {
    const onHide = () => saveNow();
    window.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  /*
   * Teardown. Runs once, on unmount.
   *
   * This SAVES the draft; it does not remove it. The only place a draft is
   * deleted is a successful submit (see PreJobCheckListV2Dialog), plus the
   * 7-day TTL sweep. An unmount is the event this whole mechanism exists to
   * survive, so the last thing it does is commit whatever the tech had typed.
   */
  useEffect(() => {
    return () => {
      const wasOpen = enabledRef.current;
      const openedAt = openedAtRef.current;
      saveNow();

      // Closing or submitting flips `enabled` to false in a render before this
      // subtree is torn down, so still being enabled here means the inspection
      // was taken away from the tech rather than dismissed by them. Navigating
      // away from the job with the dialog open lands here too, which is a small
      // false-positive rate we accept to catch the real thing.
      if (wasOpen && jobId) {
        inspectionClosedUnexpectedly(jobId, countFilledFields(valuesRef.current), openedAt ? Date.now() - openedAt : 0);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  return null;
};
